export const MESSAGE_ROLES = ["user", "agent"] as const;
export type TInterviewMessageRole = typeof MESSAGE_ROLES[number];

export const MESSAGE_CONTENT_FORMATS = ["plain", "html"] as const;
export type TInterviewMessageContentFormat = typeof MESSAGE_CONTENT_FORMATS[number];

export const GOAL_ATTACHMENT_TYPES = ["image", "audio", "video", "document", "link"] as const;
export type TInterviewGoalAttachmentType = typeof GOAL_ATTACHMENT_TYPES[number];

export const THREAD_STATUSES = [
    "waiting_for_user",
    "waiting_for_agent",
    "completed",
    "cancelled",
    "failed",
] as const;
export type TInterviewThreadStatus = typeof THREAD_STATUSES[number];

export const PROCESSING_JOB_STATUSES = [
    "pending",
    "sent",
    "processing",
    "failed",
    "done",
    "dead",
] as const;
export type TProcessingJobStatus = typeof PROCESSING_JOB_STATUSES[number];

export type TInterviewMessage = {
    id: number,
    role: TInterviewMessageRole,
    content: string,
    contentFormat: TInterviewMessageContentFormat,
    speechAvailable: boolean,
    suggestedResponses: string[],
    created?: string,
    correlationId?: string|null,
    goalAttachments?: TInterviewGoalAttachment[],
};

export type TInterviewGoalAttachment = {
    id: number,
    type: TInterviewGoalAttachmentType,
    label: string|null,
    filename: string,
    renderUrl: string|null,
    openUrl: string|null,
    url: string|null,
};

export type TInterviewGoalStatus = {
    id: number,
    description: string,
    isCompleted: boolean,
    completedAt?: string|null,
};

export type TInterviewProcessingJob = {
    id: number,
    threadId?: number|null,
    interviewId?: number|null,
    triggerMessageId?: number|null,
    type: string,
    status: string,
    createdAt: string,
    processedAt?: string|null,
    correlationId?: string|null,
};

type TInterviewThread = {
    id: number,
    status: TInterviewThreadStatus,
    startedAt?: string|null,
    messagesCount: number,
    lastActivityAt?: string|null,
    lastUserMessageAt?: string|null,
    lastAgentMessageAt?: string|null,
    completedAt?: string|null,
    closedAt?: string|null,
    messages: TInterviewMessage[],
    goals: TInterviewGoalStatus[],
    activeProcessingJob: TInterviewProcessingJob|null,
};

export function isInterviewMessageRole(value: unknown): value is TInterviewMessageRole {
    return typeof value === "string" && MESSAGE_ROLES.includes(value as TInterviewMessageRole);
}

export function isInterviewMessageContentFormat(value: unknown): value is TInterviewMessageContentFormat {
    return typeof value === "string"
        && MESSAGE_CONTENT_FORMATS.includes(value as TInterviewMessageContentFormat);
}

export function isInterviewGoalAttachmentType(value: unknown): value is TInterviewGoalAttachmentType {
    return typeof value === "string"
        && GOAL_ATTACHMENT_TYPES.includes(value as TInterviewGoalAttachmentType);
}

export function isInterviewThreadStatus(value: unknown): value is TInterviewThreadStatus {
    return typeof value === "string" && THREAD_STATUSES.includes(value as TInterviewThreadStatus);
}

export function isProcessingJobStatus(value: unknown): value is TProcessingJobStatus {
    return typeof value === "string" && PROCESSING_JOB_STATUSES.includes(value as TProcessingJobStatus);
}

export default TInterviewThread;
